import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, KeyRound, Loader2, RotateCcw, Save, SearchX, X } from "lucide-react";

export function classNames(...xs: (string | false | null | undefined)[]) {
    return xs.filter(Boolean).join(" ");
}

export type SettingsTone = "cyan" | "violet" | "emerald" | "amber" | "rose" | "neutral";
export type SettingsMotion = "drift" | "pulse" | "scan" | "orbit" | "shimmer" | "ember";

const toneStyles: Record<SettingsTone, { icon: string; title: string; accent: string; accentSoft: string }> = {
    cyan: {
        icon: "text-cyan-300",
        title: "text-cyan-200",
        accent: "#22d3ee",
        accentSoft: "rgba(34,211,238,0.16)",
    },
    violet: {
        icon: "text-violet-300",
        title: "text-violet-200",
        accent: "#a855f7",
        accentSoft: "rgba(168,85,247,0.16)",
    },
    emerald: {
        icon: "text-emerald-300",
        title: "text-emerald-200",
        accent: "#34d399",
        accentSoft: "rgba(52,211,153,0.15)",
    },
    amber: {
        icon: "text-amber-300",
        title: "text-amber-200",
        accent: "#facc15",
        accentSoft: "rgba(250,204,21,0.16)",
    },
    rose: {
        icon: "text-rose-300",
        title: "text-rose-200",
        accent: "#ff2d75",
        accentSoft: "rgba(255,45,117,0.18)",
    },
    neutral: {
        icon: "text-neutral-400",
        title: "text-neutral-300",
        accent: "#a3a3a3",
        accentSoft: "rgba(163,163,163,0.12)",
    },
};

type SectionProps = {
    id?: string;
    expandSignal?: string;
    title: string;
    description?: string;
    icon?: React.ReactNode;
    tone?: SettingsTone;
    motion?: SettingsMotion;
    compact?: boolean;
    children: React.ReactNode;
};

export const Section = React.memo(function Section({
    id,
    expandSignal,
    title,
    description,
    icon,
    tone = "cyan",
    motion = "drift",
    compact = false,
    children,
}: SectionProps) {
    const styles = toneStyles[tone];
    const [open, setOpen] = useState(false);
    React.useEffect(() => {
        if (expandSignal) setOpen(true);
    }, [expandSignal]);
    const cssVars = {
        "--settings-accent": styles.accent,
        "--settings-accent-soft": styles.accentSoft,
    } as React.CSSProperties;

    return (
        <section id={id} className="bs-settings-section scroll-mt-4" style={cssVars}>
            <div className={classNames("bs-settings-section-shell overflow-hidden rounded-[0.8rem] border bg-black/55 backdrop-blur-xl transition", `bs-settings-motion-${motion}`, open && "is-open")}>
                <button
                    type="button"
                    onClick={() => setOpen((value) => !value)}
                    aria-expanded={open}
                    className="relative flex w-full items-center justify-between gap-3 px-4 py-3.5 text-left"
                >
                    <span className="flex min-w-0 items-center gap-3">
                        {icon && (
                            <span className={classNames("grid h-9 w-9 shrink-0 place-items-center rounded-[0.65rem] border border-white/[0.08] bg-black/55", styles.icon)}>
                                {icon}
                            </span>
                        )}
                        <span className="min-w-0">
                            <span className={classNames("block text-sm font-black tracking-tight", styles.title)}>{title}</span>
                            {description && <span className="mt-0.5 block truncate text-xs font-semibold text-neutral-600">{description}</span>}
                        </span>
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                        <span className="hidden h-2 w-2 rounded-full bg-[var(--settings-accent)] shadow-[0_0_16px_var(--settings-accent)] sm:block" />
                        <ChevronDown size={18} className={classNames("text-neutral-500 transition-transform", open && "rotate-180 text-white")} />
                    </span>
                </button>
                {open && (
                    <div className="relative border-t border-white/[0.06]">
                        {children}
                    </div>
                )}
            </div>
        </section>
    );
});

type SettingRowProps = {
    label: string;
    right?: React.ReactNode;
    helper?: React.ReactNode;
    icon?: React.ReactNode;
    compact?: boolean;
};

export const SettingRow = React.memo(function SettingRow({
    label,
    right,
    helper,
    icon,
    compact = false,
}: SettingRowProps) {
    return (
        <div className={classNames("bs-settings-row border-b border-white/[0.06] bg-transparent transition last:border-b-0 hover:bg-white/[0.035]", compact ? "px-4 py-3" : "px-4 py-3.5")}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-2.5">
                    {icon && <span className="grid h-7 w-7 shrink-0 place-items-center text-neutral-500">{icon}</span>}
                    <span className="min-w-0 text-sm font-semibold text-neutral-100">{label}</span>
                </div>
                {right && <div className="min-w-0 sm:shrink-0">{right}</div>}
            </div>
            {helper && <div className={classNames("mt-1.5 text-xs leading-5 text-neutral-500", Boolean(icon) && "sm:ml-9")}>{helper}</div>}
        </div>
    );
});

type ToggleProps = { checked: boolean; onChange: () => void; ariaLabel?: string; disabled?: boolean };
export const Toggle = React.memo(function Toggle({
    checked,
    onChange,
    ariaLabel,
    disabled,
}: ToggleProps) {
    return (
        <button
            type="button"
            aria-label={ariaLabel}
            aria-pressed={checked}
            onClick={onChange}
            disabled={disabled}
            className={classNames(
                "relative h-7 w-12 rounded-full border p-1 transition",
                disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer",
                checked
                    ? "border-cyan-300/40 bg-cyan-300/20 shadow-[0_0_20px_rgba(34,211,238,0.18)]"
                    : "border-neutral-700 bg-neutral-900"
            )}
        >
            <span
                className={classNames(
                    "block h-5 w-5 rounded-full bg-white shadow transition-transform",
                    checked ? "translate-x-5" : "translate-x-0"
                )}
            />
        </button>
    );
});

export function SettingsSkeleton() {
    return (
        <div className="space-y-4">
            <div className="rounded-[2rem] border border-cyan-400/10 bg-cyan-950/[0.04] p-5">
                <div className="h-8 w-40 animate-pulse rounded-xl bg-white/10" />
                <div className="mt-3 h-4 w-64 max-w-full animate-pulse rounded-lg bg-white/5" />
            </div>
            {[0, 1, 2].map((item) => (
                <div key={item} className="rounded-[1.65rem] border border-neutral-800 bg-neutral-950/60 p-5">
                    <div className="h-5 w-36 animate-pulse rounded-lg bg-white/10" />
                    <div className="mt-5 space-y-3">
                        <div className="h-14 animate-pulse rounded-2xl bg-white/[0.04]" />
                        <div className="h-14 animate-pulse rounded-2xl bg-white/[0.04]" />
                    </div>
                </div>
            ))}
        </div>
    );
}

export function SettingsToast({ message }: { message: null | { kind: "ok" | "err"; text: string } }) {
    if (!message) return null;
    const ok = message.kind === "ok";
    return (
        <div
            role="status"
            aria-live="polite"
            className={classNames(
                "fixed left-1/2 top-4 z-[140] flex max-w-[calc(100vw-2rem)] -translate-x-1/2 items-center gap-2 rounded-2xl border px-4 py-3 text-sm font-bold shadow-2xl backdrop-blur-xl",
                ok
                    ? "border-emerald-400/30 bg-emerald-950/80 text-emerald-100 shadow-emerald-950/40"
                    : "border-rose-400/30 bg-rose-950/80 text-rose-100 shadow-rose-950/40"
            )}
        >
            {ok ? <CheckCircle2 size={17} /> : <AlertTriangle size={17} />}
            <span>{message.text}</span>
        </div>
    );
}

export type ConfirmState = {
    title: string;
    message: React.ReactNode;
    confirmLabel: string;
    tone?: "danger" | "neutral";
    requireText?: string;
    requirePassword?: boolean;
    onConfirm: () => Promise<void> | void;
};

export function ConfirmDialog({
    confirm,
    busy,
    typed,
    password,
    onTyped,
    onPassword,
    onCancel,
    onConfirm,
}: {
    confirm: ConfirmState | null;
    busy: boolean;
    typed: string;
    password?: string;
    onTyped: (value: string) => void;
    onPassword?: (value: string) => void;
    onCancel: () => void;
    onConfirm: () => void;
}) {
    if (!confirm) return null;
    const danger = confirm.tone === "danger";
    const blocked = Boolean(confirm.requireText && typed !== confirm.requireText) || Boolean(confirm.requirePassword && !password);
    return (
        <div className="fixed inset-0 z-[130] flex items-end justify-center bg-black/80 p-3 backdrop-blur-md sm:items-center">
            <div className="w-full max-w-md rounded-[2rem] border border-cyan-400/15 bg-[radial-gradient(circle_at_0%_0%,rgba(34,211,238,0.10),transparent_32%),radial-gradient(circle_at_100%_0%,rgba(244,63,94,0.10),transparent_30%),rgba(3,3,3,0.98)] p-5 shadow-[0_30px_100px_rgba(0,0,0,0.85)]">
                <div className="flex items-start justify-between gap-3">
                    <span className={classNames("grid h-12 w-12 place-items-center rounded-2xl border", danger ? "border-rose-400/35 bg-rose-400/10 text-rose-200" : "border-cyan-400/30 bg-cyan-400/10 text-cyan-200")}>
                        <AlertTriangle size={22} />
                    </span>
                    <button type="button" onClick={onCancel} className="grid h-10 w-10 place-items-center rounded-2xl text-neutral-500 transition hover:bg-white/5 hover:text-white" aria-label="Close dialog">
                        <X size={18} />
                    </button>
                </div>
                <h3 className="mt-5 text-2xl font-black tracking-tight text-white">{confirm.title}</h3>
                <div className="mt-3 text-sm leading-6 text-neutral-400">{confirm.message}</div>
                {confirm.requireText && (
                    <div className="mt-5">
                        <label className="text-xs font-black uppercase tracking-[0.22em] text-neutral-500">Type to confirm</label>
                        <input
                            value={typed}
                            onChange={(event) => onTyped(event.target.value)}
                            className="mt-2 w-full rounded-2xl border border-neutral-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-rose-400/60"
                            placeholder={confirm.requireText}
                        />
                    </div>
                )}
                {confirm.requirePassword && (
                    <div className="mt-4">
                        <label className="text-xs font-black uppercase tracking-[0.22em] text-neutral-500">Current password</label>
                        <input
                            type="password"
                            autoComplete="current-password"
                            value={password || ""}
                            onChange={(event) => onPassword?.(event.target.value)}
                            className="mt-2 w-full rounded-2xl border border-neutral-800 bg-black px-4 py-3 text-sm text-white outline-none focus:border-rose-400/60"
                            placeholder="Enter current password"
                        />
                    </div>
                )}
                <div className="mt-6 grid gap-2">
                    <button
                        type="button"
                        disabled={busy || blocked}
                        onClick={onConfirm}
                        className={classNames(
                            "flex h-12 items-center justify-center rounded-2xl text-sm font-black transition disabled:cursor-not-allowed disabled:opacity-50",
                            danger
                                ? "bg-rose-600 text-white hover:bg-rose-500"
                                : "bg-cyan-300 text-black hover:bg-cyan-200"
                        )}
                    >
                        {busy ? <Loader2 size={18} className="animate-spin" /> : confirm.confirmLabel}
                    </button>
                    <button type="button" disabled={busy} onClick={onCancel} className="h-11 rounded-2xl border border-neutral-800 text-sm font-bold text-neutral-400 transition hover:bg-white/[0.03] hover:text-white">
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
}

export type PasswordFormState = {
    currentPassword: string;
    recoveryPhrase?: string;
    newPassword: string;
    confirmPassword: string;
};

export function PasswordChangeDialog({
    open,
    value,
    saving,
    error,
    onChange,
    onClose,
    onSubmit,
}: {
    open: boolean;
    value: PasswordFormState;
    saving: boolean;
    error: string;
    onChange: (next: PasswordFormState) => void;
    onClose: () => void;
    onSubmit: () => void;
}) {
    if (!open) return null;
    const update = (key: keyof PasswordFormState) => (event: React.ChangeEvent<HTMLInputElement>) => {
        onChange({ ...value, [key]: event.target.value });
    };

    const fields: Array<{
        key: keyof PasswordFormState;
        label: string;
        helper: string;
        autoComplete: string;
    }> = [
        { key: "currentPassword", label: "Current password", helper: "Verify this session.", autoComplete: "current-password" },
        { key: "recoveryPhrase", label: "Recovery phrase", helper: "Enter all 12 words in order.", autoComplete: "off" },
        { key: "newPassword", label: "New password", helper: "Use 8-100 characters with a letter and number.", autoComplete: "new-password" },
        { key: "confirmPassword", label: "Confirm password", helper: "Repeat the new password.", autoComplete: "new-password" },
    ];

    return (
        <div
            className="fixed inset-0 z-[125] flex items-end justify-center bg-black/75 px-3 py-4 backdrop-blur-md sm:items-center"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !saving) onClose();
            }}
        >
            <form
                role="dialog"
                aria-modal="true"
                aria-label="Change password"
                className="max-h-[calc(100dvh-1.5rem)] w-full max-w-lg overflow-y-auto rounded-[1.55rem] border border-emerald-300/15 bg-[#05070b]/95 shadow-[0_0_44px_rgba(0,0,0,0.7)]"
                onSubmit={(event) => {
                    event.preventDefault();
                    onSubmit();
                }}
            >
                <div className="relative overflow-hidden border-b border-white/[0.06] px-4 py-4">
                    <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_16%_0%,rgba(52,211,153,0.16),transparent_34%),linear-gradient(120deg,transparent,rgba(52,211,153,0.06),transparent)]" />
                    <div className="relative flex items-center justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-3">
                            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[0.9rem] bg-emerald-300/10 text-emerald-100 shadow-[0_0_18px_rgba(52,211,153,0.12)]">
                                <KeyRound size={18} />
                            </span>
                            <div className="min-w-0">
                                <div className="text-lg font-black text-white">Change password</div>
                                <p className="mt-1 text-xs leading-5 text-neutral-500">
                                    Update the key for this account.
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={saving}
                            className="grid h-9 w-9 shrink-0 place-items-center text-neutral-500 transition hover:text-white disabled:opacity-50"
                            aria-label="Close password dialog"
                        >
                            <X size={18} />
                        </button>
                    </div>
                </div>

                <div className="space-y-3 px-4 py-4">
                    {fields.map((field) => (
                        <label
                            key={field.key}
                            className="block rounded-[1.15rem] bg-black/45 p-3.5 ring-1 ring-white/[0.06] transition focus-within:ring-emerald-300/35"
                        >
                            <span className="mb-2 flex items-center justify-between gap-3">
                                <span className="inline-flex min-w-0 items-center gap-2 text-[11px] font-black uppercase tracking-[0.14em] text-emerald-100">
                                    <KeyRound size={13} />
                                    {field.label}
                                </span>
                                <span className="hidden text-[10px] font-black uppercase tracking-[0.12em] text-neutral-600 sm:inline">
                                    Secure
                                </span>
                            </span>
                            <input
                                type="password"
                                autoComplete={field.autoComplete}
                                autoCapitalize={field.key === "recoveryPhrase" ? "none" : undefined}
                                spellCheck={field.key === "recoveryPhrase" ? false : undefined}
                                value={value[field.key] || ""}
                                onChange={update(field.key)}
                                className="w-full bg-transparent text-sm font-semibold text-white outline-none placeholder:text-neutral-600"
                            />
                            <span className="mt-1.5 block text-xs font-semibold leading-5 text-neutral-600">
                                {field.helper}
                            </span>
                        </label>
                    ))}

                    {error && (
                        <p role="alert" className="rounded-xl border border-rose-400/20 bg-rose-950/25 px-3 py-2 text-xs font-bold text-rose-100">
                            {error}
                        </p>
                    )}
                </div>

                <div className="flex flex-col gap-2 border-t border-white/[0.06] px-4 py-4 sm:flex-row">
                    <button
                        type="button"
                        disabled={saving}
                        onClick={onClose}
                        className="rounded-xl border border-neutral-800 bg-black px-4 py-3 text-sm font-black text-neutral-300 transition hover:bg-white/[0.04] disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={saving}
                        className="flex min-h-12 flex-1 items-center justify-center rounded-xl bg-emerald-300/15 px-4 py-3 text-sm font-black text-emerald-50 shadow-[0_0_20px_rgba(52,211,153,0.14)] transition hover:bg-emerald-300/20 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                        {saving ? <Loader2 size={18} className="animate-spin" /> : "Save password"}
                    </button>
                </div>
            </form>
        </div>
    );
}

export function DirtySettingsBar({
    saving,
    disabled,
    onSave,
    onReset,
}: {
    saving: boolean;
    disabled: boolean;
    onSave: () => void;
    onReset: () => void;
}) {
    return (
        <div className="fixed inset-x-3 bottom-[calc(5.9rem+env(safe-area-inset-bottom))] z-[70] mx-auto max-w-2xl rounded-[1.35rem] border border-cyan-400/15 bg-black/90 p-2 shadow-[0_18px_70px_rgba(0,0,0,0.72)] backdrop-blur-xl sm:bottom-5">
            <div className="flex items-center gap-2">
                <button
                    type="button"
                    disabled={disabled || saving}
                    onClick={onSave}
                    className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-2xl bg-cyan-300 px-4 text-sm font-black text-black transition hover:bg-cyan-200 disabled:cursor-not-allowed disabled:opacity-50"
                >
                    {saving ? <Loader2 size={17} className="animate-spin" /> : <Save size={17} />}
                    {saving ? "Saving" : "Save"}
                </button>
                <button
                    type="button"
                    disabled={saving}
                    onClick={onReset}
                    className="grid h-11 w-11 place-items-center rounded-2xl border border-neutral-800 text-neutral-300 transition hover:bg-white/[0.04] disabled:opacity-50"
                    title="Reset changes"
                    aria-label="Reset changes"
                >
                    <RotateCcw size={17} />
                </button>
            </div>
        </div>
    );
}

export function EmptySearchState({ query }: { query: string }) {
    return (
        <div className="rounded-[1.65rem] border border-neutral-800 bg-neutral-950/70 p-8 text-center">
            <SearchX className="mx-auto text-neutral-500" size={28} />
            <h2 className="mt-4 text-lg font-black text-white">No setting found</h2>
            <p className="mt-2 text-sm text-neutral-500">Nothing matched "{query}". Try privacy, theme, export, or notifications.</p>
        </div>
    );
}
